import Image from 'next/image';
import { Banknote, RefreshCw, ShieldCheck, Truck, X } from 'lucide-react';
import { priceFormatSuperscript } from '@/lib/price';
import type { TradeInQuoteT } from '@/lib/trade-in-quote';
import { priceFormater } from '@/settings';
import i18n from '@/settings/i18n';

const t = i18n.init('buyback');

const BENEFITS = [
  { label: 'Get paid by bank transfer or PayPal', Icon: Banknote },
  { label: 'Free, insured shipping', Icon: Truck },
  { label: 'Your data is wiped securely', Icon: ShieldCheck },
] as const;

const formatValue = (value: number) =>
  priceFormatSuperscript(priceFormater.long(value));

/**
 * Every trade-in, newest first, each with Remove, and their combined value.
 * "Add more trade-ins" goes on to the widget.
 */
export const TradeInOfferContent = ({
  onClose,
  quotes,
  onRemove,
  onAddMore,
}: {
  onClose: () => void;
  quotes: TradeInQuoteT[];
  onRemove: (quote: TradeInQuoteT) => void;
  onAddMore: () => void;
}) => (
  <div className="flex h-full flex-col p-5 text-sm text-gray-700">
    <div className="flex items-center justify-between gap-4">
      <h2 className="text-base font-medium">{t`Your trade-in offer`}</h2>
      <button
        type="button"
        onClick={onClose}
        aria-label={t`Close`}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-700 text-white hover:bg-gray-700/90"
      >
        <X className="h-4 w-4" />
      </button>
    </div>

    <div className="mt-5 rounded-xl border border-[#D9E3DF] bg-[linear-gradient(308deg,_#FFF_48.09%,_#F2FFF9_104.3%)] p-5">
      <ul className="divide-y divide-[rgba(179,_213,_203,_0.50)] border-b border-[rgba(179,_213,_203,_0.50)]">
        {[...quotes].reverse().map((quote) => (
          <li key={quote.id} className="flex items-start gap-3 py-3 first:pt-0">
            {quote.thumbnail ? (
              <Image
                src={quote.thumbnail}
                alt=""
                aria-hidden="true"
                width={40}
                height={40}
                className="h-10 w-10 shrink-0 object-contain"
                unoptimized
              />
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-700 text-white">
                <RefreshCw className="h-4 w-4" strokeWidth={2.5} />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block font-medium">{quote.name}</span>
              {quote.condition && (
                <span className="mt-0.5 block text-xs text-gray-700/60">
                  {t`${{ condition: quote.condition }} condition`}
                </span>
              )}
            </span>
            <span className="flex shrink-0 flex-col items-end gap-1">
              <span className="font-medium text-[#128452]" translate="no">
                {formatValue(quote.value)}
              </span>
              <button
                type="button"
                onClick={() => onRemove(quote)}
                className="text-xs underline hover:text-gray-700/80"
              >
                {t`Remove`}
              </button>
            </span>
          </li>
        ))}
      </ul>

      <p className="mt-4 font-medium">{t`Congrats, here's your offer!`}</p>
      <p
        className="mt-1 text-3xl font-bold leading-none text-[#128452]"
        translate="no"
      >
        {formatValue(quotes.reduce((sum, quote) => sum + quote.value, 0))}
      </p>
      <p className="mt-1.5 text-xs font-medium text-[#128452]">
        {t`Trade-in value`}
      </p>

      <ul className="mt-5 flex flex-col gap-1.5 text-[13px]">
        {BENEFITS.map(({ label, Icon }) => (
          <li key={label} className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0" />
            {t(label)}
          </li>
        ))}
      </ul>
    </div>

    <div className="mt-auto flex flex-col gap-3 pt-5">
      <button
        type="button"
        onClick={onAddMore}
        className="h-12 rounded-lg bg-gray-700 font-medium text-white transition duration-150 hover:bg-gray-700/90"
      >
        {t`Add more trade-ins`}
      </button>
      <button
        type="button"
        onClick={onClose}
        className="h-12 rounded-lg border border-[#E6E7EB] font-medium transition duration-150 hover:border-gray-700"
      >
        {t`Close`}
      </button>
    </div>
  </div>
);
